/**
 * MCP Apps (SEP-1865) Server Routes
 *
 * Provides an endpoint for serving widget HTML.
 * Widgets are expected to use the official SDK (@modelcontextprotocol/ext-apps)
 * which handles JSON-RPC communication with the host.
 */

import { Hono } from "hono";
import "../../../types/hono";
import { logger } from "../../../utils/logger";
import { getRequestLogger } from "../../../utils/request-logger";
import { classifyWidgetError } from "../../../utils/error-classify";
import { RESOURCE_MIME_TYPE } from "@modelcontextprotocol/ext-apps/app-bridge";
import type {
  McpUiResourceCsp,
  McpUiResourceMeta,
} from "@modelcontextprotocol/ext-apps";
import { MCP_APPS_SANDBOX_PROXY_HTML } from "../SandboxProxyHtml.bundled";
import { RECORDER_SHIM_JS } from "./recorder-shim";
import { injectOpenAICompat } from "../../../utils/widget-helpers";

const apps = new Hono();

/**
 * SEP-1865 mandated mimetype for MCP Apps
 * @see https://github.com/anthropics/anthropic-cookbook/blob/main/misc/sep-1865-mcp-apps.md
 */
const MCP_APPS_MIMETYPE = RESOURCE_MIME_TYPE;

/**
 * Mimetypes accepted by the unified widget-content route.
 *
 * - `text/html;profile=mcp-app` — SEP-1865 canonical.
 * - `text/html+skybridge` — legacy Apps SDK mimetype shipped by ChatGPT
 *   apps (see examples/chatgpt-apps/CoffeeShop/server.ts). Accepted for
 *   backward compatibility ONLY; strict SEP-1865 conformance reports
 *   this as a warning via `mimeTypeWarning`.
 * - `text/html` — older Apps SDK / hand-rolled widgets that just declare
 *   plain HTML. Accepted for backward compatibility ONLY; strict
 *   conformance reports this as a warning via `mimeTypeWarning`.
 *
 * Anything else triggers a hard load error so a misconfigured resource
 * doesn't silently render the wrong content type. `LEGACY_*` constants
 * are tagged so the conformance suite can distinguish "accepted with
 * warning" from "canonical".
 */
const LEGACY_SKYBRIDGE_MIMETYPE = "text/html+skybridge";
const LEGACY_PLAIN_HTML_MIMETYPE = "text/html";
const LEGACY_WIDGET_MIMETYPES: ReadonlySet<string> = new Set<string>([
  LEGACY_SKYBRIDGE_MIMETYPE,
  LEGACY_PLAIN_HTML_MIMETYPE,
]);
const ACCEPTED_WIDGET_MIMETYPES = new Set<string>([
  MCP_APPS_MIMETYPE,
  ...LEGACY_WIDGET_MIMETYPES,
]);

/**
 * CSP mode types - matches client-side CspMode type
 */
type CspMode = "permissive" | "widget-declared";
type MetadataSource = "content" | "listing" | "legacy" | "mixed" | "none";

interface WidgetContentRequest {
  serverId: string;
  resourceUri: string;
  toolInput: Record<string, unknown>;
  toolOutput: unknown;
  /**
   * Tool response `_meta` (Apps SDK contract). When present, the compat
   * runtime exposes it as `window.openai.toolResponseMetadata` so widgets
   * can read timestamps / source IDs without digging into toolOutput.
   */
  toolResponseMetadata?: Record<string, unknown> | null;
  /**
   * Persisted widget state from a saved view or fork. Seeds
   * `window.openai.widgetState` in the compat runtime so widgets boot
   * in the previously-saved state rather than fresh defaults.
   */
  initialWidgetState?: unknown;
  toolId: string;
  toolName: string;
  theme?: "light" | "dark";
  cspMode?: CspMode;
  /**
   * When true, injects the OpenAI Apps SDK `window.openai` shim into
   * the widget HTML before returning it. Default `false` — only hosts
   * that emulate ChatGPT/Copilot or MCPJam's dev surface should opt in.
   * The flag is resolved client-side from the active host config's
   * `mcpProfile.apps.compatRuntime` (with host style preset fallback)
   * so absent / non-boolean payloads here mean "no shim".
   */
  injectOpenAiCompat?: boolean;
  /**
   * Resolved per-method `window.openai.*` capability surface. Caller
   * (client renderer) computes this from the active host config's
   * preset + override stack and forwards it verbatim. The local server
   * route does NOT resolve from a hostConfig — it doesn't own one —
   * so missing payloads here mean "use the SDK runtime's default full
   * surface", preserving legacy behavior.
   *
   * When present, methods whose capability resolves false will be
   * absent on `window.openai` in the widget (feature-detection truth).
   */
  openAiCompatCapabilities?: Record<string, unknown>;
  template?: string;
  viewMode?: string;
  viewParams?: Record<string, unknown>;
}

/**
 * Fallback CSP extraction for legacy Apps SDK widgets that declare CSP
 * via `_meta["openai/widgetCSP"]` (snake_case fields:
 * `connect_domains`, `resource_domains`, `frame_domains`) instead of the
 * SEP-1865 `_meta.ui.csp` shape (camelCase). Returns the camelCase shape
 * the proxy's buildCSP expects, or undefined when no legacy CSP is set
 * or only contains non-array values.
 */
function extractLegacyOpenAICsp(
  resourceMeta: Record<string, unknown> | undefined
): McpUiResourceCsp | undefined {
  if (!resourceMeta) return undefined;
  const legacy = resourceMeta["openai/widgetCSP"];
  if (!legacy || typeof legacy !== "object") return undefined;
  const src = legacy as Record<string, unknown>;
  const readArr = (v: unknown): string[] | undefined =>
    Array.isArray(v)
      ? v.filter((x): x is string => typeof x === "string" && x.length > 0)
      : undefined;
  const out: McpUiResourceCsp = {};
  const connect = readArr(src.connect_domains);
  const resource = readArr(src.resource_domains);
  const frame = readArr(src.frame_domains);
  if (connect && connect.length > 0) out.connectDomains = connect;
  if (resource && resource.length > 0) out.resourceDomains = resource;
  if (frame && frame.length > 0) out.frameDomains = frame;
  return Object.keys(out).length > 0 ? out : undefined;
}

// Serve widget content with CSP metadata (SEP-1865)
apps.post("/widget-content", async (c) => {
  try {
    const body = (await c.req.json()) as Partial<WidgetContentRequest>;
    const {
      serverId,
      resourceUri,
      toolInput,
      toolOutput,
      toolResponseMetadata,
      initialWidgetState,
      toolId,
      toolName,
      theme,
      cspMode,
      template: templateUri,
      viewMode,
      viewParams,
      injectOpenAiCompat,
      openAiCompatCapabilities,
    } = body;

    if (!serverId || !resourceUri || !toolId || !toolName) {
      return c.json({ error: "Missing required fields" }, 400);
    }
    if (templateUri && !templateUri.startsWith("ui://")) {
      return c.json({ error: "Template must use ui:// protocol" }, 400);
    }

    // SEP-1865 hardening: cspMode MUST be passed explicitly by the
    // renderer — the previous implicit `"permissive"` default
    // accidentally widened the policy when a caller forgot to set it.
    // Spec-mode hosts that don't declare CSP fall through to the
    // sandbox-proxy's restrictive defaults; permissive mode remains
    // available for debugging but must be opt-in.
    if (cspMode !== "permissive" && cspMode !== "widget-declared") {
      return c.json(
        {
          error:
            "cspMode is required and must be 'permissive' or 'widget-declared'",
        },
        400
      );
    }

    const resolvedResourceUri = templateUri || resourceUri;

    const effectiveCspMode = cspMode;
    const mcpClientManager = c.mcpClientManager;

    // REUSE existing mcpClientManager.readResource (same as resources.ts)
    const resourceResult = await mcpClientManager.readResource(serverId, {
      uri: resolvedResourceUri,
    });

    // Extract HTML from resource contents
    const contents = resourceResult?.contents || [];
    const content = contents[0];

    if (!content) {
      getRequestLogger(c, "routes.apps.mcp-apps").event(
        "widget.resource.failed",
        {
          widgetType: "mcp_apps",
          resourceUri: resolvedResourceUri,
          cspMode: effectiveCspMode,
          errorCode: classifyWidgetError(null, "resource_missing"),
        }
      );
      return c.json({ error: "No content in resource" }, 404);
    }

    // Accept the SEP-1865 canonical mimetype plus the two legacy Apps SDK
    // forms (`text/html+skybridge` and plain `text/html`) so widgets that
    // worked on the old ChatGPTAppRenderer path keep rendering through
    // the consolidated MCP path. Anything else is a hard error.
    const contentMimeType = (content as { mimeType?: string }).mimeType;
    const mimeTypeValid =
      typeof contentMimeType === "string" &&
      ACCEPTED_WIDGET_MIMETYPES.has(contentMimeType);
    const mimeTypeWarning = !mimeTypeValid
      ? contentMimeType
        ? `Invalid mimetype "${contentMimeType}" - expected one of: ${[
            ...ACCEPTED_WIDGET_MIMETYPES,
          ].join(", ")}`
        : `Missing mimetype - expected one of: ${[
            ...ACCEPTED_WIDGET_MIMETYPES,
          ].join(", ")}`
      : contentMimeType !== MCP_APPS_MIMETYPE
      ? `Legacy Apps SDK mimetype "${contentMimeType}" — SEP-1865 prefers "${MCP_APPS_MIMETYPE}"`
      : null;

    if (mimeTypeWarning) {
      logger.warn("[MCP Apps] Mimetype validation: " + mimeTypeWarning, {
        resourceUri: resolvedResourceUri,
      });
    }
    if (!mimeTypeValid) {
      return c.json({ error: mimeTypeWarning }, 415);
    }

    let html: string;
    if ("text" in content && typeof content.text === "string") {
      html = content.text;
    } else if ("blob" in content && typeof content.blob === "string") {
      html = Buffer.from(content.blob, "base64").toString("utf-8");
    } else {
      getRequestLogger(c, "routes.apps.mcp-apps").event(
        "widget.resource.failed",
        {
          widgetType: "mcp_apps",
          resourceUri: resolvedResourceUri,
          cspMode: effectiveCspMode,
          errorCode: classifyWidgetError(null, "html_missing"),
        }
      );
      return c.json({ error: "No HTML content in resource" }, 404);
    }

    // SEP-1865 effective UI metadata resolution (PR 2026-01):
    //   1. content-item `_meta.ui` from the `resources/read` content   (canonical)
    //   2. listing `_meta.ui` from `resources/list`                    (fallback)
    //   3. legacy Apps SDK `openai/widget*` keys on either source      (last resort)
    //
    // The fallback chain is per-field, not all-or-nothing: a widget
    // that publishes only `csp` at the content level and only
    // `prefersBorder` at the listing level should see both honored.
    // Listing lookup is best-effort — older servers that don't
    // implement `resources/list` (or that fail to return the URI) just
    // leave the listing source undefined and the content source wins.
    const resourceMeta = content._meta as Record<string, unknown> | undefined;
    const contentUiMeta = (
      resourceMeta as { ui?: McpUiResourceMeta } | undefined
    )?.ui;

    let listingMeta: Record<string, unknown> | undefined;
    let listingUiMeta: McpUiResourceMeta | undefined;
    const metadataSources: {
      csp: Exclude<MetadataSource, "mixed">;
      permissions: Exclude<MetadataSource, "mixed">;
      prefersBorder: Exclude<MetadataSource, "mixed">;
    } = {
      csp: "none",
      permissions: "none",
      prefersBorder: "none",
    };
    try {
      const listing = await mcpClientManager.listResources(serverId);
      const match = listing?.resources?.find(
        (r: { uri?: unknown }) => r?.uri === resolvedResourceUri
      ) as { _meta?: Record<string, unknown> } | undefined;
      if (match?._meta) {
        listingMeta = match._meta;
        listingUiMeta = (match._meta as { ui?: McpUiResourceMeta }).ui;
      }
    } catch (err) {
      logger.debug("[MCP Apps] resources/list fallback skipped", {
        resourceUri: resolvedResourceUri,
        reason: err instanceof Error ? err.message : String(err),
      });
    }

    const contentLegacyCsp = extractLegacyOpenAICsp(resourceMeta);
    const listingLegacyCsp = extractLegacyOpenAICsp(listingMeta);
    let csp: McpUiResourceCsp | undefined;
    if (contentUiMeta?.csp) {
      csp = contentUiMeta.csp;
      metadataSources.csp = "content";
    } else if (listingUiMeta?.csp) {
      csp = listingUiMeta.csp;
      metadataSources.csp = "listing";
    } else if (contentLegacyCsp) {
      csp = contentLegacyCsp;
      metadataSources.csp = "legacy";
    } else if (listingLegacyCsp) {
      csp = listingLegacyCsp;
      metadataSources.csp = "legacy";
    }
    const permissions =
      contentUiMeta?.permissions ?? listingUiMeta?.permissions;
    if (contentUiMeta?.permissions) {
      metadataSources.permissions = "content";
    } else if (listingUiMeta?.permissions) {
      metadataSources.permissions = "listing";
    }
    const prefersBorderLegacy = (m: Record<string, unknown> | undefined) =>
      typeof m?.["openai/widgetPrefersBorder"] === "boolean"
        ? (m["openai/widgetPrefersBorder"] as boolean)
        : undefined;
    const contentLegacyPrefersBorder = prefersBorderLegacy(resourceMeta);
    const listingLegacyPrefersBorder = prefersBorderLegacy(listingMeta);
    let prefersBorder: boolean | undefined;
    if (contentUiMeta?.prefersBorder !== undefined) {
      prefersBorder = contentUiMeta.prefersBorder;
      metadataSources.prefersBorder = "content";
    } else if (listingUiMeta?.prefersBorder !== undefined) {
      prefersBorder = listingUiMeta.prefersBorder;
      metadataSources.prefersBorder = "listing";
    } else if (contentLegacyPrefersBorder !== undefined) {
      prefersBorder = contentLegacyPrefersBorder;
      metadataSources.prefersBorder = "legacy";
    } else if (listingLegacyPrefersBorder !== undefined) {
      prefersBorder = listingLegacyPrefersBorder;
      metadataSources.prefersBorder = "legacy";
    }

    const usedMetadataSources = new Set(
      Object.values(metadataSources).filter((source) => source !== "none")
    );
    const metadataSource: MetadataSource =
      usedMetadataSources.size === 0
        ? "none"
        : usedMetadataSources.size === 1
        ? Array.from(usedMetadataSources)[0]
        : "mixed";

    // Log CSP and permissions configuration for security review (SEP-1865)
    logger.debug("[MCP Apps] Security configuration", {
      resourceUri: resolvedResourceUri,
      effectiveCspMode,
      metadataSource,
      metadataSources,
      widgetDeclaredCsp: csp
        ? {
            connectDomains: csp.connectDomains || [],
            resourceDomains: csp.resourceDomains || [],
            frameDomains: csp.frameDomains || [],
            baseUriDomains: csp.baseUriDomains || [],
          }
        : null,
      widgetDeclaredPermissions: permissions
        ? {
            camera: permissions.camera !== undefined,
            microphone: permissions.microphone !== undefined,
            geolocation: permissions.geolocation !== undefined,
            clipboardWrite: permissions.clipboardWrite !== undefined,
          }
        : null,
    });

    // When in permissive mode, skip CSP entirely (for testing/debugging)
    // When in widget-declared mode, use the widget's CSP metadata (or restrictive defaults)
    const isPermissive = effectiveCspMode === "permissive";

    // Optionally inject the OpenAI Apps SDK `window.openai` shim.
    // Claude/Cursor/Codex-style hosts leave this surface off; ChatGPT/
    // Copilot and MCPJam's dev surface opt in. The renderer resolves
    // the flag from the active host config and forwards it here, so the
    // injection decision travels with the request rather than living on
    // the server.
    const shouldInjectOpenAiCompat = injectOpenAiCompat === true;
    if (shouldInjectOpenAiCompat) {
      html = injectOpenAICompat(html, {
        toolId,
        toolName,
        toolInput: toolInput ?? {},
        toolOutput,
        toolResponseMetadata: toolResponseMetadata ?? null,
        initialWidgetState: initialWidgetState ?? null,
        theme,
        viewMode,
        viewParams,
        // Cast: the wire body declares `Record<string, unknown>` for
        // forward compatibility (server doesn't structurally validate
        // the matrix here — the SDK runtime accepts a sparse partial
        // and fills in defaults). Type-checking happens client-side.
        capabilities: openAiCompatCapabilities as
          | Parameters<typeof injectOpenAICompat>[1]["capabilities"]
          | undefined,
      });
    }

    // Return JSON with HTML and metadata for CSP enforcement
    getRequestLogger(c, "routes.apps.mcp-apps").event(
      "widget.resource.served",
      {
        widgetType: "mcp_apps",
        resourceUri: resolvedResourceUri,
        cspMode: effectiveCspMode,
        injectedOpenAiCompat: shouldInjectOpenAiCompat,
        mimeTypeValid,
      }
    );
    c.header("Cache-Control", "no-cache, no-store, must-revalidate");
    return c.json({
      html,
      csp: isPermissive ? undefined : csp,
      permissions, // Include permissions metadata
      permissive: isPermissive, // Tell sandbox-proxy to skip CSP injection entirely
      cspMode: effectiveCspMode,
      prefersBorder,
      // Echoed for trace clarity. The renderer's reload-key already
      // uses the flag it sent (not this echoed value), but persisting
      // the server-confirmed value alongside cached HTML makes saved
      // views unambiguous about their contents.
      injectedOpenAiCompat: shouldInjectOpenAiCompat,
      // Per plan §6.5: replay/debug needs to know *which* surface was
      // injected, not just yes/no. Echo the capability record alongside
      // the boolean so saved views are self-describing. Absent when
      // injection was off or when no capabilities were passed (legacy
      // call sites — defaults to full surface, which is implied).
      injectedOpenAiCompatCapabilities:
        shouldInjectOpenAiCompat && openAiCompatCapabilities !== undefined
          ? openAiCompatCapabilities
          : undefined,
      // SEP-1865 mimetype validation
      mimeType: contentMimeType,
      mimeTypeValid,
      mimeTypeWarning,
      // SEP-1865 metadata precedence. `metadataSource` is a summary
      // ("mixed" when per-field fallbacks used different sources);
      // `metadataSources` reports the actual source for each field.
      metadataSource,
      metadataSources,
    });
  } catch (error) {
    getRequestLogger(c, "routes.apps.mcp-apps").event(
      "widget.resource.failed",
      {
        widgetType: "mcp_apps",
        errorCode: classifyWidgetError(error),
      }
    );
    logger.error("[MCP Apps] Error fetching resource", error);
    return c.json(
      { error: error instanceof Error ? error.message : "Unknown error" },
      500
    );
  }
});

// Inject the Tier 2 recorder shim (a JS string from recorder-shim.ts) into the
// proxy's `RECORDER_SHIM` placeholder at serve time, so the heavy, unit-tested
// shim source lives in one TS module and never drifts from a copy in the HTML.
const SANDBOX_PROXY_HTML_WITH_RECORDER = MCP_APPS_SANDBOX_PROXY_HTML.replace(
  '"__MCPJAM_RECORDER_SHIM__"',
  () => JSON.stringify(RECORDER_SHIM_JS)
);

apps.get("/sandbox-proxy", (c) => {
  c.header("Content-Type", "text/html; charset=utf-8");
  c.header("Cache-Control", "no-cache, no-store, must-revalidate");
  c.header(
    "Content-Security-Policy",
    "frame-ancestors 'self' http://localhost:* http://127.0.0.1:* https://localhost:* https://127.0.0.1:*"
  );
  c.res.headers.delete("X-Frame-Options");
  return c.body(SANDBOX_PROXY_HTML_WITH_RECORDER);
});

export default apps;
